import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Scale, Bell, Plus, Trash2, ArrowLeft, Loader2, RefreshCw, CalendarClock, CheckCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function Alerts() {
  const [watches, setWatches] = useState([]);
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cnr: "", lawyer_name: "", lawyer_number: "" });
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, l] = await Promise.all([api.get("/watches"), api.get("/alerts/log")]);
      setWatches(w.data);
      setLog(l.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const addWatch = async () => {
    if (!form.cnr.trim() || !form.lawyer_number.trim()) {
      toast.error("CNR and lawyer WhatsApp number are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/watches", form);
      toast.success("Case added to watchlist");
      setForm({ cnr: "", lawyer_name: "", lawyer_number: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/watches/run-check");
      toast.success(`Checked ${data.checked} case(s) · ${data.alerts} alert(s) sent`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/watches/${deleteTarget.id}`);
      toast.success("Removed from watchlist");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      setDeleteTarget(null);
    }
  };

  return (
    <div className="min-h-screen bg-[--w-bg] pb-16 text-[--w-text]">
      <header className="sticky top-0 z-30 border-b border-[--w-border] bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 ring-1 ring-blue-200">
              <Scale className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-[--w-text]">Cause List Alerts</h1>
              <p className="font-mono-data text-[11px] text-[--w-text-3]">Auto-ping lawyers when a watched case is listed</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="run-check-button"
              onClick={runCheck}
              disabled={checking}
              variant="secondary"
              className="h-9 bg-stone-700 text-white hover:bg-stone-600"
            >
              {checking ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
              Run check now
            </Button>
            <Link
              to="/"
              data-testid="back-to-dashboard"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] space-y-5 px-5 pt-6">
        <div className="flex items-start gap-2 rounded-lg border border-[--w-border] bg-[--w-card] p-4 text-xs text-[--w-text-2]">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <p>
            Add a case by its 16-character <span className="font-mono-data text-[--w-text-2]">CNR</span> and the lawyer's WhatsApp number.
            A daily job checks the eCourts cause list and messages the lawyer when the case is listed for hearing.
            Use <span className="text-[--w-text-2]">Run check now</span> to test immediately. Cases are also <span className="text-[--w-text-2]">added here automatically</span> when a lawyer looks them up by CNR on WhatsApp. (eCourts cause-list checks consume API credits.)
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border border-[--w-border] bg-[--w-card]">
          <div className="flex items-center justify-between border-b border-[--w-border] p-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-[--w-text-2]" />
              <span className="font-display text-lg font-bold text-[--w-text]">Watchlist</span>
              <span className="ml-1 rounded-full bg-stone-100 px-2 py-0.5 font-mono-data text-xs text-[--w-text-2]">{watches.length}</span>
            </div>
            <Button data-testid="add-watch-button" onClick={() => setOpen(true)} className="h-9 bg-blue-600 text-white hover:bg-blue-700">
              <Plus className="mr-1.5 h-4 w-4" /> Watch Case
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[--w-border] text-xs uppercase tracking-wider text-[--w-text-3]">
                  <th className="px-4 py-3 font-medium">CNR</th>
                  <th className="px-4 py-3 font-medium">Lawyer</th>
                  <th className="px-4 py-3 font-medium">WhatsApp</th>
                  <th className="px-4 py-3 font-medium">Last Checked</th>
                  <th className="px-4 py-3 font-medium">Alerts</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[--w-border]" data-testid="watch-table-body">
                {watches.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-[--w-text-3]">No cases watched yet.</td></tr>
                )}
                {watches.map((w) => (
                  <tr key={w.id} data-testid={`watch-row-${w.id}`} className="transition-colors duration-200 hover:bg-[--w-card-2]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono-data text-xs font-semibold text-blue-600">{w.cnr}</span>
                        {w.auto_added && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-blue-700" data-testid={`auto-badge-${w.id}`}>auto</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[--w-text]">{w.lawyer_name || "—"}</td>
                    <td className="px-4 py-3 font-mono-data text-xs text-[--w-text-2]">{w.lawyer_number}</td>
                    <td className="px-4 py-3 text-xs text-[--w-text-2]">{fmt(w.last_checked_at)}</td>
                    <td className="px-4 py-3 font-mono-data text-xs text-[--w-text-2]">{w.alerts_sent}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`delete-watch-${w.id}`}
                        onClick={() => setDeleteTarget(w)}
                        className="rounded p-1.5 text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-[--w-border] bg-[--w-card]">
          <div className="border-b border-[--w-border] p-4">
            <h3 className="font-display text-lg font-bold text-[--w-text]">Alert Log</h3>
            <p className="mt-0.5 text-xs text-[--w-text-2]">Hearing alerts sent to lawyers</p>
          </div>
          <div className="max-h-80 space-y-3 overflow-y-auto p-4" data-testid="alert-log">
            {log.length === 0 && <p className="py-6 text-center text-sm text-[--w-text-3]">No alerts sent yet.</p>}
            {log.map((a) => (
              <div key={a.id} className="rounded-lg border border-[--w-border] bg-[--w-bg] p-3 fade-up">
                <div className="flex items-center justify-between">
                  <span className="font-mono-data text-xs font-semibold text-blue-600">{a.cnr}</span>
                  <div className="flex items-center gap-2">
                    {a.delivery_channel === "simulated" && (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] uppercase text-amber-700">simulated</span>
                    )}
                    {a.delivered && (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-700"><CheckCheck className="h-3 w-3" /> delivered</span>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-[11px] text-[--w-text-3]">
                  {a.lawyer_name || "Lawyer"} · <span className="font-mono-data">{a.lawyer_number}</span> · {fmt(a.created_at)}
                </div>
                <p className="mt-2 whitespace-pre-wrap rounded bg-[--w-card] p-2 text-xs text-[--w-text-2]">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[--w-border] bg-[--w-card] text-[--w-text] sm:max-w-md" data-testid="add-watch-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Watch a Case</DialogTitle>
            <DialogDescription className="text-[--w-text-2]">
              We'll alert the lawyer on WhatsApp whenever this case appears on a daily cause list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-[--w-text-2]">CNR (16 characters)</Label>
              <Input data-testid="watch-cnr" value={form.cnr} onChange={(e) => setForm({ ...form, cnr: e.target.value.toUpperCase() })} placeholder="DLHC010001232024" className="mt-1 border-[--w-border] bg-[--w-bg] font-mono-data text-sm" />
            </div>
            <div>
              <Label className="text-xs text-[--w-text-2]">Lawyer Name</Label>
              <Input data-testid="watch-lawyer-name" value={form.lawyer_name} onChange={(e) => setForm({ ...form, lawyer_name: e.target.value })} placeholder="Adv. Priya Menon" className="mt-1 border-[--w-border] bg-[--w-bg] text-sm" />
            </div>
            <div>
              <Label className="text-xs text-[--w-text-2]">Lawyer WhatsApp Number</Label>
              <Input data-testid="watch-lawyer-number" value={form.lawyer_number} onChange={(e) => setForm({ ...form, lawyer_number: e.target.value })} placeholder="+919820012345" className="mt-1 border-[--w-border] bg-[--w-bg] font-mono-data text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]">Cancel</Button>
            <Button data-testid="save-watch-button" onClick={addWatch} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to Watchlist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-[--w-border] bg-[--w-card] text-[--w-text]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Stop watching this case?</AlertDialogTitle>
            <AlertDialogDescription className="text-[--w-text-2]">
              <span className="font-mono-data text-blue-600">{deleteTarget?.cnr}</span> will no longer trigger hearing alerts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[--w-border] bg-transparent text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-watch" onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
